import { useContext, useState, useEffect, ChangeEvent } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { FormGroup, FormControlLabel, FormControl, TextField, Select, MenuItem, Checkbox, Container, Grid, Button, AppBar, Toolbar, Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import { Person } from '@mui/icons-material';
import { HtmlTooltip } from './HtmlTooltip';
import { Context } from '../context/IntlContext';
import { AppVersionContext } from '../context/AppVersionContext';
import { getSetupInstructionsPDF } from './GolferInfo';
import * as FitterHelper from '../library/FitterHelper';
import * as TutorialHelper from '../library/TutorialHelper';
import SimulatorConfiguration, * as SimulatorHelper from '../library/SimulatorHelper';
import * as IosHelper from '../library/IosHelper';
import { logDev } from '../library/Globals';
import Fitter from '../classes/Fitter';
import * as TrackMan from '../classes/TrackMan';
import { Locale, LocaleType } from '../classes/Locale';

export default function FitterProcess() {
    const { appVersion } = useContext(AppVersionContext);
    const [fitter, setFitter] = useState(new Fitter());
    const [ws, setWs] = useState({} as WebSocket);
    const [isConnected, setIsConnected] = useState(false);
    const [showIsConnectedMessage, setShowIsConnectedMessage] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialId, setTutorialId] = useState(0);
    const [showVersion, setShowVersion] = useState(false);
    const [showBadHttpsSettings, setShowBadHttpsSettings] = useState(false);
    const [simulatorConfiguration, setSimulatorConfiguration] = useState(new SimulatorConfiguration());
    const [iOS, setIOS] = useState(false);
    const [setupInstructionsPDF, setSetupInstructionsPDF] = useState();

    const [searchParams] = useSearchParams();

    const intl = useIntl();
    const intlUseEstimate = intl.formatMessage({ id: "settings.use.estimate" });
    const intlHideGolferEmail = intl.formatMessage({ id: "settings.hide.golfer.email" });
    const intlShowSm11 = intl.formatMessage({ id: "settings.show.sm11" });
    const context = useContext(Context);
    const showSm11 = true; 
    
    useEffect((): void => {
        (window as any).iOSMessage = iOSMessage;
        
        const getAsyncData = async () => {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'vwf_fittingprocess'
                }
            })
            
            setShowTutorial(await TutorialHelper.isActive());

            const newFitter = await FitterHelper.getFitter();
            setFitter(newFitter);

            // Get "Setup Instructions" pdf url
            const setupInstructionsPDF = getSetupInstructionsPDF(newFitter.locale);
            setSetupInstructionsPDF(setupInstructionsPDF);

            let newSimulatorConfiguration = await SimulatorHelper.getConfiguration();
            setSimulatorConfiguration(newSimulatorConfiguration);
            setIOS(await IosHelper.get());
        }

        getAsyncData();
    }, []);

    let navigate = useNavigate();

    const handleCancel = () => {
        wsClose();
        navigate('/apps/wedgefitting/golferinfo');
    }

    const handleSubmit = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_update_settings',
                q_speed: fitter.speedUnits,
                q_distance: fitter.distanceUnits,
                q_language: fitter.language,
                q_fitting_type: fitter.fittingType,
                q_shots: fitter.shotsPerClub,
                q_use_estimate: fitter.useEstimateDynamicLoft.toString(),
                q_hideEmail: fitter.hideGolferEmail.toString()
            }
        })
        SimulatorHelper.saveConfiguration(simulatorConfiguration);
        FitterHelper.upsertFitter(fitter);
        context.selectLocale(fitter.locale);
        wsClose();
        navigate('/apps/wedgefitting/golferinfo');
    }

    const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        //log GA event
        switch (target.name) {
            case "modelType":
                TagManager.dataLayer({
                    dataLayer: {
                        event: 'vwf_model_change'
                    }
                })
                break;
        }
        let newFitter = { ...fitter, [target.name]: target.value } as Fitter;
        setFitter(newFitter);
    }

    const handleSelectLocale = (event: ChangeEvent<HTMLInputElement>): void => {
        setFitter({ ...fitter, locale: event.target.value as LocaleType });
    }

    const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>): void => {
        let newFitter = { ...fitter, [event.target.name]: event.target.checked } as Fitter;
        setFitter(newFitter);
    }

    const handleSm11Change = (event: ChangeEvent<HTMLInputElement>): void => {
        let newFitter = { ...fitter, showSm11: event.target.checked } as Fitter;
        setFitter(newFitter);
    }

    const handleTrackManInputChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        let newSimulatorConfiguration = { ...simulatorConfiguration, [target.name]: target.value } as SimulatorConfiguration;
        setSimulatorConfiguration(newSimulatorConfiguration);
    }

    const handleFitterSettings = () => {
        setShowVersion(!showVersion);
    }

    const handleTestConnectionButton = () => {
        setShowSpinner(true);
        setShowIsConnectedMessage(false);
        setShowBadHttpsSettings(false);
        //send message to iOS if necessary
        if (fitter.iOS) {
            try {
                (window as any).webkit.messageHandlers.iOSRequestStatus.postMessage(true);
            }
            catch { }
        } else {
            //no iOS app, talk to TM directly
            setTimeout(() => {
                connectToTrackMan();
            }, 1000);
        }
    }

    const iOSMessage = (msg: string) => {
        try {
            let data = JSON.parse(msg) as TrackMan.Data;
            const isConnected = data.SubType === "connected";
            setShowSpinner(false);
            setIsConnected(isConnected);
            setShowIsConnectedMessage(true);
            sentGtmConnected(isConnected);
            if (!isConnected) {
                (window as any).webkit.messageHandlers.iOSConnectTM.postMessage(true);
            }
        }
        catch (err) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'iOS: receive message',
                    msg: 'catch: ' + err
                }
            });
        }
    }

    const wsClose = () => {
        try {
            ws.close();
        }
        catch { }
    }

    const connectToTrackMan = async () => {
        let socket: WebSocket;
        try {
            let simulator = await SimulatorHelper.getSimulator(simulatorConfiguration);
            let url = simulator.getUrl(simulatorConfiguration);
            socket = new WebSocket(url);
            setWs(socket);

            socket.onopen = async function (e) {
                //only TrackMan is connected via onopen() event
                if (simulator.modelType === "trackman") {
                    setConnectionStatus(true);
                }

                //after two seconds (an abitrary number), close the websocket
                setTimeout(() => {
                    wsClose();
                }, 2000);
            };

            socket.onmessage = function (msg) {
                if (simulator.modelType === "foresight") {
                    logDev(msg.data);
                    if (msg.data) {
                        let msgData = msg.data.toLowerCase();
                        if (msgData.includes("status")) {
                            let isConnected = (msgData.includes(" connected"));
                            setConnectionStatus(isConnected);
                        }
                    }
                } else {
                    if (msg.data.includes("ping")) {

                    }
                }
            }

            socket.onerror = function (err) {
                sentGtmConnected(false);
                setShowSpinner(false);
                setIsConnected(false);
                setShowIsConnectedMessage(true);
            };
        }
        catch (err) {
            TagManager.dataLayer({
                dataLayer: {
                    event: 'log_error',
                    description: 'Fitting Process - socket error',
                    msg: 'catch: ' + err
                }
            });
            sentGtmConnected(false);
            setShowSpinner(false);
            setIsConnected(false);
            if (err.message.indexOf('insecure WebSocket') > -1) {
                setShowBadHttpsSettings(true);
            } else {
                setShowIsConnectedMessage(true);
            }
        }


    }

    const setConnectionStatus = async (status: boolean) => {
        setShowSpinner(false);
        setIsConnected(status);
        sentGtmConnected(false);
        setShowIsConnectedMessage(true);
        //success! save the configuration since we know it works
        await SimulatorHelper.saveConfiguration(simulatorConfiguration);
    }

    const sentGtmConnected = (deviceIsConnected = false) => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_monitor_connection',
                q_method: 'Test Connection',
                q_connection: deviceIsConnected.toString(),
                q_model: simulatorConfiguration.modelType,
                q_connection_type: simulatorConfiguration.connectionType
            }
        });
    }

    const showPopup = (id: number) => {
        return showTutorial && tutorialId == id;
    }

    const handleTutorialNext = () => {
        if (tutorialId === 2) {
            TutorialHelper.disable();
            navigate('/apps/wedgefitting/golferinfo');
        } else {
            setTutorialId(tutorialId + 1);
        }
    }

    const handleTutorialCancel = async () => {
        await TutorialHelper.disable();
        setShowTutorial(false);
        navigate('/apps/wedgefitting/golferinfo');
    }

    return (
        <>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ display: { xs: 'none', md: 'flex' } }}>
                                        <Person sx={{ width: '30px', marginRight: '5px', marginTop: '0px', height: '2em' }}></Person>
                                        <span style={{ marginTop: '10px' }}>{fitter.firstName} {fitter.lastName}</span>
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', marginLeft: '50px', marginTop: '10px' }} onClick={handleFitterSettings}>
                                        <FormattedMessage id="settings.title" />
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} container justifyContent="flex-end">
                                    <Button disableRipple type="submit" variant="contained" onClick={handleCancel} className='cancel-button'><FormattedMessage id="general.cancel" /></Button>
                                    <Button disableRipple type="submit" variant="contained" onClick={handleSubmit} className='submit-button' sx={{ width: '175px !important' }}><FormattedMessage id="settings.update" /></Button>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>

            <Container maxWidth='lg' className="fitting-process">
                <HtmlTooltip
                    title={
                        <>
                            <Typography color="inherit" sx={{ marginBottom: '10px' }}><FormattedMessage id="quick.start.reviewing.guide" /></Typography>
                            <Typography color="inherit" sx={{ marginBottom: '10px' }}><FormattedMessage id="quick.start.helpful" /></Typography>
                            <Typography color="inherit" sx={{ height: '100px' }}><FormattedMessage id="quick.start.questions" /></Typography>
                            <div style={{ textAlign: 'right' }}>
                                <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.finish" /></Button>
                            </div>
                        </>
                    }
                    open={showPopup(2)}
                    placement={'bottom'}
                    arrow={false}
                    maxwidth={800}
                >
                    <Grid container className='row-error' sx={{ marginTop: '75px !important' }}>
                    </Grid>
                </HtmlTooltip>

                <Grid container sx={{ marginTop: '50px', backgroundColor: 'transparent', textAlign: 'left' }}>
                    <Grid item xs={12} className="header"><b><span><FormattedMessage id="settings.launch.monitor" /></span></b></Grid>

                    <Grid container maxWidth="lg">
                        <Grid item xs={3} sx={{ minHeight: "72px" }}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <FormattedMessage id="settings.model" />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <Select
                                            value={simulatorConfiguration.modelType}
                                            name="modelType"
                                            onChange={handleTrackManInputChange}
                                            className="select"
                                            size="small"
                                        >
                                            <MenuItem value={'TrackMan'}><FormattedMessage id="settings.trackman" /></MenuItem>
                                            {!iOS && (
                                                <MenuItem value={'Foresight'}><FormattedMessage id="settings.foresight" /></MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>

                        <HtmlTooltip
                            title={
                                <>
                                    <Typography color="inherit"><FormattedMessage id="quick.start.use.tool" /></Typography>
                                    <br />
                                    <div style={{ textAlign: 'right' }}>
                                        <Button disableRipple onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                        <Button disableRipple onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                    </div>
                                </>
                            }
                            open={showPopup(0)}
                            placement={'right'}
                            arrow={true}
                            maxwidth={400}
                        >

                            <Grid item xs={2}>
                                <Grid container>
                                    <Grid item>
                                        <FormattedMessage id="settings.connection" />
                                    </Grid>
                                    <Grid item>
                                        <FormControl >
                                            <Select
                                                value={simulatorConfiguration.connectionType}
                                                name="connectionType"
                                                onChange={handleTrackManInputChange}
                                                className="select"
                                                size="small"
                                                sx={{ width: '175px !important' }}
                                            >
                                                <MenuItem value={'Ethernet'}><FormattedMessage id="settings.ethernet" /></MenuItem>
                                                <MenuItem value={'WiFi'}><FormattedMessage id="settings.wifi" /></MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </HtmlTooltip>

                        <Grid item xs={2}>
                            <Grid container>
                                {simulatorConfiguration.modelType === "TrackMan" && simulatorConfiguration.connectionType === 'Ethernet' && <>
                                    <Grid item xs={12} sx={{ textAlign: 'left' }}>
                                        <Typography><FormattedMessage id="settings.ethernet.address" /></Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <TextField
                                                value={simulatorConfiguration.ethernetAddress}
                                                margin="normal"
                                                fullWidth
                                                name="ethernetAddress"
                                                onChange={handleTrackManInputChange}
                                                size='small'
                                                sx={{ marginTop: '0px', width: '150px' }}
                                            />
                                        </FormControl>

                                    </Grid>
                                </>}
                            </Grid>

                        </Grid>
                    </Grid>
                    <Grid container>
                    </Grid>
                    <Grid item xs={3} sx={{ minHeight: '55px' }}>
                        <Grid container>
                            <Button
                                disableRipple
                                className='submit-button'
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 1, fontSize: '20px', width: '175px !important' }}
                                onClick={handleTestConnectionButton}
                            >
                                <FormattedMessage id="settings.test.connection" />
                            </Button>

                            {showSpinner && <>
                                <span className="sk-wave" style={{ display: 'inline-flex', marginLeft: '10px', marginTop: '20px' }} >
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                    <span className="sk-wave-rect" style={{ backgroundColor: 'black' }}></span>
                                </span>
                            </>}


                        </Grid>
                    </Grid>
                    <Grid item xs={9}>
                        {simulatorConfiguration.modelType === "TrackMan" && simulatorConfiguration.connectionType === 'Ethernet' && <>
                            <span><FormattedMessage id="settings.download.utility.trackman" /> </span><a style={{ color: 'red' }} target="_blank" href="https://surefithub.titleist.com/apps/trackman-finder/index.html"><FormattedMessage id="settings.download.utility.here" /></a>
                        </>}
                        {simulatorConfiguration.modelType === 'Foresight' && <>
                            <span><FormattedMessage id="settings.download.utility.foresight" /> </span><a style={{ color: 'red' }} target="_blank" href="https://surefithub.titleist.com/apps/fs-bridge/index.html"><FormattedMessage id="settings.download.utility.here" /></a>
                        </>}

                        <span>&nbsp;</span>
                    </Grid>

                    <Grid item xs={12} sx={{ marginBottom: "20px" }}>
                        {showIsConnectedMessage && isConnected && <>
                            <Typography sx={{ fontColor: 'green', marginTop: "5px" }}><FormattedMessage id="settings.connection.successful" /></Typography>
                        </>}
                        {simulatorConfiguration.modelType === "TrackMan" && showIsConnectedMessage && !isConnected && <>
                            <Typography sx={{ color: 'red', marginTop: "5px" }}><FormattedMessage id="settings.connection.failed" /></Typography>
                        </>}
                        {simulatorConfiguration.modelType === "Foresight" && showIsConnectedMessage && !isConnected && <>
                            <Typography sx={{ color: 'red', marginTop: "5px" }}>Connection failed. Please check the Foresight device.</Typography>
                        </>}
                        {(showBadHttpsSettings && !isConnected && !iOS) && (
                            <span style={{ cursor: "pointer" }}>
                                <p style={{ color: 'red', marginTop: "5px" }}><FormattedMessage id="settings.connection.blocked" /></p>
                                <span><FormattedMessage id="settings.connection.blocked.info" /> </span><span style={{ fontStyle: "underline" }} onClick={() => window.open("/apps/wedgefitting/setupinstructions")}><u><FormattedMessage id="settings.connection.setup.instructions" /></u></span>
                            </span>
                        )}
                        {(showBadHttpsSettings && !isConnected && iOS) && (
                            <a href={setupInstructionsPDF} target="_blank" style={{ fontSize: '12px' }} rel="noreferrer">
                                <FormattedMessage id="settings.connection.setup.instructions" />
                            </a>
                        )}
                    </Grid>
                </Grid>

                <Grid item xs={12} className="header"><b><span><FormattedMessage id="settings.uom" /></span></b></Grid>

                <Grid container className='row'>
                    <Grid item xs={12} sm={4} sx={{ textAlign: 'left' }}>
                        <Grid container>
                            <Grid item xs={12}>
                                <FormattedMessage id="settings.speed" />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Select
                                        value={fitter.speedUnits}
                                        name="speedUnits"
                                        onChange={handleInputChange}
                                        className="select"
                                        size="small"
                                    >
                                        <MenuItem value={'MPH'}><FormattedMessage id="settings.mph" /></MenuItem>
                                        <MenuItem value={'M/S'}><FormattedMessage id="settings.mps" /></MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} sm={4} sx={{ textAlign: 'left' }}>
                        <Grid container>
                            <Grid item xs={12}>
                                <FormattedMessage id="settings.distance" />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Select
                                        value={fitter.distanceUnits}
                                        name="distanceUnits"
                                        onChange={handleInputChange}
                                        className="select"
                                        size="small"
                                    >
                                        <MenuItem value={'Yards'}><FormattedMessage id="settings.yards" /></MenuItem>
                                        <MenuItem value={'Meters'}><FormattedMessage id="settings.meters" /></MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} sm={4} sx={{ textAlign: 'left' }}>
                        <Grid container>
                            <Grid item xs={12}>
                                <FormattedMessage id="settings.language" />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Select
                                        value={fitter.locale}
                                        name="locale"
                                        onChange={handleSelectLocale}
                                        className="select"
                                        size="small"
                                    >
                                        <MenuItem value={Locale.english}><FormattedMessage id="settings.english" /></MenuItem>
                                        <MenuItem value={Locale.french}><FormattedMessage id="settings.french" /></MenuItem>
                                        <MenuItem value={Locale.frenchCanadian}><FormattedMessage id="settings.french.canadian" /></MenuItem>
                                        <MenuItem value={Locale.german}><FormattedMessage id="settings.german" /></MenuItem>
                                        <MenuItem value={Locale.japanese}><FormattedMessage id="settings.japanese" /></MenuItem>
                                        <MenuItem value={Locale.korean}><FormattedMessage id="settings.korean" /></MenuItem>
                                        <MenuItem value={Locale.italian}><FormattedMessage id="settings.italian" /></MenuItem>
                                        <MenuItem value={Locale.spanish}><FormattedMessage id="settings.spanish" /></MenuItem>
                                        <MenuItem value={Locale.thai}><FormattedMessage id="settings.thai" /></MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                </Grid>

                <Grid item xs={12} sx={{ marginBottom: "20px", minHeight: '30px' }}>
                </Grid>
                <Grid item xs={12} className="header"><b><span><FormattedMessage id="settings.fitting.process" /></span></b></Grid>

                <Grid container className='row'>
                    <Grid item xs={12} sm={4} sx={{ textAlign: 'left' }}>
                        <Grid container>
                            <Grid item xs={12}>
                                <FormattedMessage id="settings.fitting.type" />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Select
                                        value={fitter.fittingType}
                                        name="fittingType"
                                        onChange={handleInputChange}
                                        className="select"
                                        size="small"
                                    >
                                        <MenuItem value={'Standard'}><FormattedMessage id="settings.standard" /></MenuItem>
                                        <MenuItem value={'Detailed'}><FormattedMessage id="settings.detailed" /></MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                    <HtmlTooltip
                        title={
                            <>
                                <Typography color="inherit"><FormattedMessage id="quick.start.choose.fitting" /></Typography>
                                <br />
                                <div style={{ textAlign: 'right' }}>
                                    <Button onClick={handleTutorialCancel} className="cancel-button tutorial"><FormattedMessage id="general.close" /></Button>
                                    <Button onClick={handleTutorialNext} className="submit-button tutorial"><FormattedMessage id="general.next" /></Button>
                                </div>
                            </>
                        }
                        open={showPopup(1)}
                        placement={'right'}
                        arrow={true}
                        maxwidth={350}
                    >

                        <Grid item xs={12} sm={4} sx={{ textAlign: 'left' }}>
                            <Grid container>
                                <Grid item xs={12}>
                                    <FormattedMessage id="settings.shots.per.club" />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <Select
                                            value={fitter.shotsPerClub}
                                            name="shotsPerClub"
                                            onChange={handleInputChange}
                                            className="select"
                                            size="small"
                                        >
                                            <MenuItem value={3}>3</MenuItem>
                                            <MenuItem value={5}>5</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Grid>
                    </HtmlTooltip>

                </Grid>

                <Grid container className='row' sx={{ marginTop: "20px" }}>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox sx={{ '&.Mui-checked': { color: 'gray' } }} name="useEstimateDynamicLoft" onChange={handleCheckboxChange} checked={fitter.useEstimateDynamicLoft} />} label={`${intlUseEstimate}`} />
                        </FormGroup>
                    </Grid>
                </Grid>

                <Grid container className='row' sx={{ marginTop: "20px" }}>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox sx={{ '&.Mui-checked': { color: 'gray' } }} name="hideGolferEmail" onChange={handleCheckboxChange} checked={fitter.hideGolferEmail} />} label={`${intlHideGolferEmail}`} />
                        </FormGroup>
                    </Grid>
                </Grid>

                {false && showSm11 && (
                    <Grid container className='row' sx={{ marginTop: "20px" }}>
                        <Grid item xs={12}>
                            <FormGroup>
                                <FormControlLabel control={<Checkbox sx={{ '&.Mui-checked': { color: 'gray' } }} name="showSm11" onChange={handleSm11Change} checked={fitter.showSm11} />} label={`${intlShowSm11}`} />
                            </FormGroup>
                        </Grid>
                    </Grid>
                )}

                {showVersion &&
                    <Grid container className='row' sx={{ marginTop: "20px" }}>
                        <Grid item xs={12}>
                            <Typography>v{appVersion}</Typography>
                        </Grid>
                    </Grid>
                }

            </Container>
        </>
    )
}
